/**
 * Google Sign-In — chỉ dùng REACT_APP_GOOGLE_CLIENT_ID từ .env (CRA inject lúc build).
 * Không hardcode Client ID trong source.
 */
function normalizeEnv(value) {
  return value != null ? String(value).trim() : "";
}

function isLikelyLocalhost(hostname) {
  if (!hostname) return true;
  if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1") return true;
  if (hostname.endsWith(".local")) return true;
  return false;
}

function getGoogleClientId() {
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  const isLocal = isLikelyLocalhost(hostname);

  const devId = normalizeEnv(process.env.REACT_APP_GOOGLE_CLIENT_ID_DEV);
  const prodId = normalizeEnv(process.env.REACT_APP_GOOGLE_CLIENT_ID_PROD);
  const legacyId = normalizeEnv(process.env.REACT_APP_GOOGLE_CLIENT_ID);

  if (isLocal) return devId || legacyId;
  return prodId || legacyId;
}

function assertGoogleClientId() {
  if (!getGoogleClientId()) {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    throw new Error(
      `Thiếu Google Client ID cho môi trường hiện tại${origin ? ` (${origin})` : ""}. ` +
        "Hãy set REACT_APP_GOOGLE_CLIENT_ID_DEV/REACT_APP_GOOGLE_CLIENT_ID_PROD (hoặc REACT_APP_GOOGLE_CLIENT_ID) trong .env."
    );
  }
}

let isGoogleLoaded = false;
let googleLoadPromise = null;

function loadGoogleScript() {
  if (googleLoadPromise) return googleLoadPromise;

  googleLoadPromise = new Promise((resolve, reject) => {
    if (isGoogleLoaded) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;

    script.onload = () => {
      isGoogleLoaded = true;
      resolve();
    };

    script.onerror = () => {
      reject(new Error("Failed to load Google Identity Services"));
    };

    document.head.appendChild(script);
  });

  return googleLoadPromise;
}

async function initializeGoogleAuth() {
  assertGoogleClientId();
  await loadGoogleScript();

  return new Promise((resolve) => {
    window.google.accounts.id.initialize({
      client_id: getGoogleClientId(),
      callback: () => {},
    });
    resolve();
  });
}

export async function signInWithGooglePopup() {
  assertGoogleClientId();
  await initializeGoogleAuth();

  return new Promise((resolve, reject) => {
    const clientId = getGoogleClientId();
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: (response) => {
        if (response.credential) {
          resolve({ credential: response.credential });
        } else {
          reject(new Error("No credential received"));
        }
      },
    });

    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        const buttonDiv = document.createElement("div");
        buttonDiv.style.position = "fixed";
        buttonDiv.style.top = "-1000px";
        document.body.appendChild(buttonDiv);

        window.google.accounts.id.renderButton(buttonDiv, {
          theme: "outline",
          size: "large",
          click_listener: () => {
            document.body.removeChild(buttonDiv);
          },
        });

        setTimeout(() => {
          const button = buttonDiv.querySelector('[role="button"]');
          if (button) button.click();
        }, 100);
      }
    });
  });
}

export async function signInWithGoogleOAuth() {
  assertGoogleClientId();
  const clientId = getGoogleClientId();
  const redirectUri = window.location.origin;
  const scope = "openid email profile";

  const authUrl =
    `https://accounts.google.com/o/oauth2/v2/auth?` +
    `client_id=${encodeURIComponent(clientId)}&` +
    `redirect_uri=${encodeURIComponent(redirectUri)}&` +
    `response_type=code&` +
    `scope=${encodeURIComponent(scope)}&` +
    `include_granted_scopes=true&` +
    `state=google_login`;

  return new Promise((resolve, reject) => {
    const popup = window.open(
      authUrl,
      "google-login",
      "width=500,height=600,scrollbars=yes,resizable=yes"
    );

    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        reject(new Error("Popup closed by user"));
      }
    }, 1000);

    const messageListener = (event) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === "GOOGLE_AUTH_SUCCESS") {
        clearInterval(checkClosed);
        window.removeEventListener("message", messageListener);
        popup.close();
        resolve({ credential: event.data.idToken });
      } else if (event.data.type === "GOOGLE_AUTH_ERROR") {
        clearInterval(checkClosed);
        window.removeEventListener("message", messageListener);
        popup.close();
        reject(new Error(event.data.error || "Google authentication failed"));
      }
    };

    window.addEventListener("message", messageListener);
  });
}
